// ── Collateral Management — Portfolio (landing page) ──
// One row per borrower, from their latest borrowing base certificate: where
// availability stands, how it moved since the prior BBC, and whether the BBC is
// current. This is the "across the whole book" view; the per-borrower charts
// live on the Borrower Dashboard, one click away on any row.
//
// BS LOAN BAL (TODAY) IS THE ONE LIVE NUMBER. Everything else is what the BBC
// said on its as-of date; this is the revolving line's BS Loan Balance right
// now, read from getLoans so it is the same figure the Portfolio app shows.
// The BBC's own loan balance stays alongside it, unchanged, and still drives
// Utilization and Excess. Which line: the loan the latest BBC was filed
// against, when that is a revolving line; otherwise every revolving line on
// the account, summed.
//
// A REVOLVER AT ZERO NEEDS NO BBC, so its Days Old reads N/A rather than a
// count, and it is not counted as stale.
import { useState, useEffect } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate } from "../../shared/format";
import { Btn, Card, ErrorMsg, SectionHeader, Spinner, STh, Td, TdNum } from "../../shared/ui";
import { useSortable } from "../../shared/hooks";
import { useApp } from "../../shared/appContext";

// A monthly BBC older than this is overdue. 45 days = the month plus the usual
// two-week reporting window.
const STALE_DAYS = 45;
const FUNDED = ["Accrual", "Non Accrual"];
// Borrower names longer than this are cut off with "…" (full name on hover)
// so the whole table fits the page without sideways scrolling.
const BORROWER_W = 170;
const REVOLVING = ft => /^revolving line of credit$/i.test((ft || "").trim());

// BBC requirement frequency, from the account's Borrowing Base Package covenant.
const FREQ_RANK = { "Weekly": 1, "Monthly": 2, "Quarterly": 3, "6-Months": 4, "Yearly": 5, "Set Date": 6 };
const num = v => { const n = parseFloat(v); return isFinite(n) ? n : null; };
const pct = v => v == null ? "—" : (v * 100).toFixed(1) + "%";

export const CollateralPortfolio = () => {
  const { api, go } = useApp();
  const [rows, setRows] = useState([]);
  const [loans, setLoans] = useState(null);         // null = live balances unavailable
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);   // include non-funded accounts

  const load = () => {
    setLoading(true); setErr(null);
    // Live balances are extra: if getLoans fails the table still loads with
    // the BBC figures and the live column shows a dash.
    Promise.all([
      api.get("getCollateralPortfolio"),
      api.get("getLoans").catch(() => null),
    ])
      .then(([d, l]) => {
        setRows(Array.isArray(d) ? d : []);
        setLoans(Array.isArray(l) ? l.filter(x => REVOLVING(x.facilitytype)) : null);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Live BS Loan Balance per account: the BBC's own revolving line if it has
  // one, else every revolving line on the account. null = no revolver found
  // (or live balances unavailable).
  const liveBal = (r) => {
    if (!loans) return null;
    const own = r.bbloanid != null && loans.find(l => String(l.id) === String(r.bbloanid));
    const set = own ? [own] : loans.filter(l => String(l.accountid) === String(r.accountid));
    if (!set.length) return null;
    return set.reduce((s, l) => s + (num(l.bsloanbalanceeod) || 0), 0);
  };

  // Derived columns, computed once so the table sorts on exactly what it shows.
  const data = rows
    .filter(r => showAll || FUNDED.includes(r.accountstatus))
    .map(r => {
      const avail = num(r.borrowing_base_avail), bal = num(r.current_loan_balance), excess = num(r.excess_availability);
      const prevExcess = num(r.prev_excess_availability);
      const live = liveBal(r);
      const noBbcNeeded = live != null && Math.abs(live) < 0.005;
      const days = r.days_since_asof != null && !noBbcNeeded ? Number(r.days_since_asof) : null;
      return {
        ...r,
        avail, bal, excess, live, noBbcNeeded,
        utilization: avail ? (bal || 0) / avail : null,
        excess_chg: excess != null && prevExcess != null ? excess - prevExcess : null,
        days,
        dilution: r.dilution_90d != null ? Number(r.dilution_90d) : null,
        dilution30: r.dilution_30d != null ? Number(r.dilution_30d) : null,
        dso: r.dso_90d != null ? Number(r.dso_90d) : null,
        stale: days != null && days > STALE_DAYS,
        bbc_frequency: r.bbc_frequency ? String(r.bbc_frequency).trim() : null,
        // sorts by how often, not alphabetically; no covenant sorts last
        bbc_freq_rank: FREQ_RANK[r.bbc_frequency ? String(r.bbc_frequency).trim() : ""] ?? 99,
        deficit: excess != null && excess < 0,
      };
    });

  const { sorted, sortKey, sortDir, toggle } = useSortable(data, "companyname", "asc");
  const H = ({ label, k, numeric }) =>
    <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric={numeric} />;

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const sum = k => data.reduce((s, r) => s + (r[k] || 0), 0);
  const tAvail = sum("avail"), tBal = sum("bal"), tExcess = sum("excess"), tLive = sum("live");
  const nDeficit = data.filter(r => r.deficit).length;
  const nStale = data.filter(r => r.stale).length;

  // Group headers: the one live figure on the left, everything read from the
  // latest BBC under a single "Borrowing Base Figures" banner on the right, so
  // no individual column has to say where it came from.
  const GROUP_RULE = "1.5px solid var(--color-border-secondary, #c8cdd2)";
  const groupCap = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase",
                     letterSpacing: "0.06em", marginBottom: 6 };
  const groupTh = (label, span, extra) => (
    <th colSpan={span} style={{ ...groupCap, marginBottom: 0, textAlign: "center", padding: "8px 12px 6px",
                                borderBottom: "0.5px solid var(--color-border-tertiary)", ...extra }}>{label}</th>
  );

  const tile = (label, value, color) => (
    <div style={{ flex: "1 1 160px", padding: "12px 14px", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4, color: color || "inherit", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );

  return <>
    <SectionHeader title="Collateral Portfolio" count={data.length} actions={[
      <label key="all" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
        <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
        Include non-funded accounts
      </label>,
      <Btn key="new" variant="primary" onClick={() => go("collateral-home", { newbbc: 1 })}>＋ New Borrowing Base</Btn>,
    ]} />

    <div style={{ marginBottom: 16 }}>
      <div>
        <div style={groupCap}>Borrowing Base Figures</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {tile("Borrowing Base Avail", fmt(tAvail))}
          {tile("Loan Balance", fmt(tBal))}
          {tile("Excess / (Deficit)", fmt(tExcess), tExcess < 0 ? "#993C1D" : "#0F6E56")}
          {tile("Utilization", pct(tAvail ? tBal / tAvail : null))}
          {tile("In Deficit", nDeficit, nDeficit ? "#993C1D" : "inherit")}
          {tile(`BBC > ${STALE_DAYS} Days Old`, nStale, nStale ? "#7A5200" : "inherit")}
        </div>
      </div>
    </div>

    {/* Fit the table to the page: tighter cell padding, headers allowed to
        wrap onto two lines, and the borrower name cut off with an ellipsis
        (full name on hover). Inline styles on STh/Td win over a class, hence
        !important, scoped to this table only. */}
    <style>{`
      .cp-fit th, .cp-fit td { padding-left: 8px !important; padding-right: 8px !important; }
      .cp-fit thead tr:last-child th { white-space: normal !important; vertical-align: bottom; line-height: 1.25; }
    `}</style>
    <Card style={{ overflowX: "auto" }}>
      <table className="cp-fit" style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead>
        <tr>
          <th />
          <th />
          {groupTh("Today's Balance", 1)}
          {groupTh("Borrowing Base Figures", 11, { borderLeft: GROUP_RULE })}
        </tr>
        <tr>
          <H label="Borrower" k="companyname" />
          <H label="BBC Freq." k="bbc_freq_rank" />
          <H label="BS Loan Balance" k="live" numeric />
          <H label="Latest As-of" k="asofdate" />
          <H label="Days Old" k="days" numeric />
          <H label="BB Avail" k="avail" numeric />
          <H label="Loan Balance" k="bal" numeric />
          <H label="Utilization" k="utilization" numeric />
          <H label="Excess / (Deficit)" k="excess" numeric />
          <H label="Δ vs Prior BBC" k="excess_chg" numeric />
          <H label="Dilution (30d)" k="dilution30" numeric />
          <H label="Dilution (90d)" k="dilution" numeric />
          <H label="DSO (90d)" k="dso" numeric />
          <H label="BBCs" k="bbc_count" numeric />
        </tr></thead>
        <tbody>
          {sorted.length === 0
            ? <tr><td colSpan={14} style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
                No borrowing bases yet. Click "New Borrowing Base" to load one.
              </td></tr>
            : sorted.map(r => (
              <tr key={r.accountid} style={{ cursor: "pointer", background: r.deficit ? "#FBE4DD55" : "" }}
                onClick={() => go("collateral-dashboard", { accountid: r.accountid })}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                onMouseLeave={e => e.currentTarget.style.background = r.deficit ? "#FBE4DD55" : ""}>
                <Td style={{ fontWeight: 500 }}
                  title={(r.companyname || r.accountid) + (!FUNDED.includes(r.accountstatus) && r.accountstatus ? " · " + r.accountstatus : "")}>
                  <span style={{ display: "block", maxWidth: BORROWER_W, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.companyname || r.accountid}
                    {!FUNDED.includes(r.accountstatus) &&
                      <span style={{ marginLeft: 8, fontSize: 11, color: "var(--color-text-secondary)" }}>{r.accountstatus || ""}</span>}
                  </span>
                </Td>
                <Td style={{ color: r.bbc_frequency ? "inherit" : "var(--color-text-secondary)" }}
                  title={r.bbc_frequency ? "From the account's Borrowing Base Package covenant" : "No Borrowing Base Package covenant on this account"}>
                  {r.bbc_frequency || "—"}</Td>
                <TdNum style={{ fontWeight: 500 }}>{r.live != null ? fmt(r.live) : "—"}</TdNum>
                <Td style={{ borderLeft: GROUP_RULE }}>
                  <span style={{ color: B.cyanDark, fontWeight: 500 }}
                    onClick={e => { e.stopPropagation(); go("collateral-bbc", { bbid: r.bbid }); }}>{fmtDate(r.asofdate)}</span>
                </Td>
                <TdNum style={{ color: r.noBbcNeeded ? "var(--color-text-secondary)" : r.stale ? "#7A5200" : "inherit", fontWeight: r.stale ? 600 : 400 }}
                  title={r.noBbcNeeded ? "Revolving line is at zero today — no BBC required" : undefined}>
                  {r.noBbcNeeded ? "N/A" : r.days ?? "—"}</TdNum>
                <TdNum>{r.avail != null ? fmt(r.avail) : "—"}</TdNum>
                <TdNum>{r.bal != null ? fmt(r.bal) : "—"}</TdNum>
                <TdNum style={{ color: r.utilization > 1 ? "#993C1D" : "inherit" }}>{pct(r.utilization)}</TdNum>
                <TdNum style={{ color: r.deficit ? "#993C1D" : "inherit", fontWeight: 500 }}>{r.excess != null ? fmt(r.excess) : "—"}</TdNum>
                <TdNum style={{ color: r.excess_chg == null ? "inherit" : r.excess_chg < 0 ? "#993C1D" : "#0F6E56" }}>
                  {r.excess_chg == null ? "—" : (r.excess_chg > 0 ? "+" : "") + fmt(r.excess_chg)}
                </TdNum>
                <TdNum style={{ color: r.dilution30 == null ? "inherit" : r.dilution30 > 0.10 ? "#993C1D" : r.dilution30 > 0.05 ? "#BA7517" : "inherit", fontWeight: r.dilution30 > 0.05 ? 600 : 400 }}>
                  {r.dilution30 == null ? "—" : (r.dilution30 * 100).toFixed(1) + "%"}
                  {r.dilution30 != null && r.dilution != null && Math.abs(r.dilution30 - r.dilution) >= 0.01 &&
                    <span title={"30-day vs 90-day: " + ((r.dilution30 - r.dilution) * 100).toFixed(1) + " pts"} style={{ marginLeft: 4, color: r.dilution30 > r.dilution ? "#993C1D" : "#0F6E56" }}>{r.dilution30 > r.dilution ? "▲" : "▼"}</span>}</TdNum>
                <TdNum style={{ color: r.dilution == null ? "inherit" : r.dilution > 0.10 ? "#993C1D" : r.dilution > 0.05 ? "#BA7517" : "inherit", fontWeight: r.dilution > 0.05 ? 600 : 400 }}>
                  {r.dilution == null ? "—" : (r.dilution * 100).toFixed(1) + "%"}</TdNum>
                <TdNum>{r.dso == null ? "—" : Math.round(r.dso)}</TdNum>
                <TdNum>{r.bbc_count}</TdNum>
              </tr>
            ))}
        </tbody>
      </table>
    </Card>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8 }}>
      Click a borrower for their dashboard, or the as-of date to open that certificate. Rows shaded red are in deficit;
      Days Old in amber means the latest BBC is more than {STALE_DAYS} days old; N/A means the revolving line is at zero today, so no BBC is required.
      Today's Balance is the revolving line's BS Loan Balance today; everything under Borrowing Base Figures is as reported on the latest BBC. Dilution is amber above 5% and red above 10%; ▲ / ▼ on the 30-day figure means it is running 1+ point above / below the 90-day rate.
    </div>
  </>;
};
